import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { map } from 'rxjs';
import { Oversikt } from '../../../shared/models/manuell-atgard';
import { NameType } from '../../../shared/models/models';
import { NyttStandardNamnDTO, StandardNamn } from '../../../shared/models/standard-namn';
import { SynonymDTO } from '../../../shared/models/synonymer';
import { IgnoreringDTO } from '../../models/ignore';
import { ParkeringDTO } from '../../models/parkera';
import { StandardNamnService } from '../../standard-namn/standard-namn.service';
import { AtgardsListaService } from '../atgards-lista.service';
import { DuplicateDialogComponent } from './duplicate-dialog/duplicate-dialog.components';
import { CaseSpellingDialogComponent } from './case-spelling-dialog/case-spelling-dialog.component';

export enum Atgard {
  Ignorera,
  Synonymisera,
  Parkera,
  NyttStandardnamn,
}

interface AtgardsVal {
  value: Atgard;
  label: string;
}

export interface AtgardResult {
  result: 'success' | 'error';
  message: string;
}

@Component({
    selector: 'lib-atgarda-edit-dialog',
    templateUrl: './atgarda-edit-dialog.component.html',
    styleUrls: ['./atgarda-edit-dialog.component.scss'],
    standalone: false
})
export class AtgardaEditDialogComponent implements OnInit {
  oversiktRow!: Oversikt;

  atgarder: AtgardsVal[] = [
    { value: Atgard.Ignorera, label: 'Ignorera' },
    { value: Atgard.Synonymisera, label: 'Synonymisera' },
    { value: Atgard.NyttStandardnamn, label: 'Nytt Svebarspecifikt standardnamn' },
  ];

  startdateMin = new Date(new Date().setHours(0, 0, 0, 0));
  atgardResult?: AtgardResult;
  form!: FormGroup;
  standardNamnLista: StandardNamn[] = [];
  loading = false;
  Atgard = Atgard;
  selectedAtgard!: Atgard;
  protected nameType = NameType;
  protected typeOfFynd = NameType.Smittamne;

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    public caseRef: DynamicDialogRef | null = null,
    public duplicateRef: DynamicDialogRef,
    private standardNamnService: StandardNamnService,
    private atgardsService: AtgardsListaService,
    private dialogService: DialogService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.standardNamnService.load();
    this.oversiktRow = this.config.data.row;

    this.form = new FormGroup({
      selectedAtgard: new FormControl<Atgard | null>(null, [Validators.required]),
      synonymiseraTill: new FormControl<StandardNamn | null>(null, [this.validateSynonymiseraTill.bind(this)]),
      comment: new FormControl<string | null>(''),
      standardnamn: new FormGroup({
        shortName: new FormControl<string | null>(null),
        nyttnamn: new FormControl<string | null>(null),
        smittamneType: new FormControl<string | null>(null),
        // engelskTerm: new FormControl<string | null>(null),
        // conceptId: new FormControl<string | null>(null),
        standardnameid: new FormControl<string | null>(null),
        smittamneCategory: new FormControl<string | null>(null),
        standardNamnCoupling: new FormControl<StandardNamn | null>(null),
        startdate: new FormControl<Date | null>(null),
      }),
    });

    this.updateStandardnamnList(this.oversiktRow.type as NameType);
    this.form.get('selectedAtgard')?.valueChanges.subscribe(this.onSelectedAtgardChanges.bind(this));
    const standardNameGroup = this.form.get('standardnamn')
    standardNameGroup?.get('nyttnamn')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.atgardResult = undefined);
  }

  onSelectedAtgardChanges(value: any) {
    const sdn = this.form.get('standardnamn');
    if (value !== Atgard.NyttStandardnamn) {
      sdn?.get('shortName')?.setValue(null);
      sdn?.get('nyttnamn')?.setValue(null);
      sdn?.get('smittamneType')?.setValue(null);
      sdn?.get('engelskTerm')?.setValue(null);
      sdn?.get('conceptId')?.setValue(null);
      sdn?.get('standardnameid')?.setValue(null);
      sdn?.get('smittamneCategory')?.setValue(null);
      sdn?.get('startdate')?.setValue(null);
    }
    if (value === Atgard.Synonymisera) {
      this.form.get('synonymiseraTill')?.setValue(null);
      this.selectedAtgard = Atgard.Synonymisera;
    }
    if (value === Atgard.Ignorera) {
      this.selectedAtgard = Atgard.Ignorera;
    }
    if (value === Atgard.NyttStandardnamn) {
      this.selectedAtgard = Atgard.NyttStandardnamn;
      const newName = this.oversiktRow.name?.replaceAll('"', '');
      // sdn?.get('nyttnamn')?.setValue(newName);
      // sdn?.get('nyttnamn')?.disable({ onlySelf: true });
    }
  }

  validateSynonymiseraTill(control: AbstractControl): ValidationErrors | null {
    if (!control.value && this.form?.value?.selectedAtgard === 'Synonymisera') {
      return { error: 'error' };
    }
    return null;
  }

  protected standardnamnInvalidLength() {
    const namn = this.form.get('standardnamn')?.get('nyttnamn')?.value
    const over100 = namn?.length > 100;
    const under3 = namn?.trim().length < 3;
    return over100 || under3;
  }

  useShortName(): boolean {
    return this.oversiktRow.type === NameType.Antibiotika || this.oversiktRow.type === NameType.Smittamne;
  }
  useSmittamneTyp(): boolean {
    return this.oversiktRow.type === NameType.Smittamne;
  }
  useID(): boolean {
    return this.oversiktRow.type === NameType.Smittamne || this.oversiktRow.type === NameType.Antibiotika;
  }
  useSnoMedCT(): boolean {
    return this.oversiktRow.type === NameType.Provmaterial;
  }
  useConceptID(): boolean {
    return this.oversiktRow.type === NameType.Provmaterial;
  }
  useSmittamneCategory(): boolean {
    return this.oversiktRow.type === NameType.Smittamne;
  }
  useDates(): boolean {
    return this.oversiktRow.type === NameType.Analys;
  }
  useComments(): boolean {
    return this.selectedAtgard !== null && this.selectedAtgard !== Atgard.NyttStandardnamn;
  }

  showTypeOfFynd() {
    return this.selectedAtgard === Atgard.Synonymisera && this.oversiktRow.type === NameType.Smittamne;
  }

  save() {
    this.loading = true;
    if (this.form.value.selectedAtgard === Atgard.Synonymisera) {
      const synonym = {
        synonym: this.oversiktRow.name,
        name: this.form.value.synonymiseraTill,
        comments: this.form.value.comment,
        labb_kod: this.oversiktRow.labCode,
      } as SynonymDTO;
      this.synonymisera(synonym);
    } else if (this.form.value.selectedAtgard === Atgard.Ignorera) {
      this.form.valid && (this.loading = true);

      const ignoreDTO = {
        name: this.oversiktRow.name,
        comments: this.form.value.comment,
        labb_kod: this.oversiktRow.labCode,
      } as IgnoreringDTO;
      this.ignorera(ignoreDTO);
    } else if (this.form.value.selectedAtgard === Atgard.Parkera) {
      this.form.valid && (this.loading = true);

      const parkDTO = {
        name: this.oversiktRow.name,
        comments: this.form.value.comment,
        labb_kod: this.oversiktRow.labCode,
      } as ParkeringDTO;
      this.parkera(parkDTO);
    } else if (this.form.value.selectedAtgard === Atgard.NyttStandardnamn) {
      const caseWarning = this.checkCaseSpelling(this.form.getRawValue().standardnamn.nyttnamn.trim());
      this.form.valid && (this.loading = true);

      if(caseWarning) {
        this.showCaseSpellWarningDialog();
      } else {
        this.buildAndSaveNewStandardName()
      }
    }
  }

  private checkCaseSpelling(name: string) {
    const regex = /^[A-Z][a-z]*(?: [a-z]+)*$/; //kollar att bara den första bokstaven är versal
    return !regex.test(name);
  }

  private buildAndSaveNewStandardName() {
    const standardName = {
      name: this.form.getRawValue().standardnamn.nyttnamn.trim(),
      term: this.form.getRawValue().standardnamn.nyttnamn.trim(),
      shortName: this.form.value.standardnamn.shortName,
      type: this.form.value.standardnamn.smittamneType,
      snomed_ct: this.form.value.standardnamn.engelskTerm,
      snomed_ct_concept_id: this.form.value.standardnamn.conceptId,
      id: this.form.value.standardnamn.standardnameid,
      category: this.form.value.standardnamn.smittamneCategory,
      start: this.form.value.standardnamn.startdate,
      comments: this.form.value.comment,
      standardId: this.form.value.standardnamn.standardNamnCoupling?.id,
    } as NyttStandardNamnDTO;

      this.nyttStandardNamn(standardName);
  }

  showCaseSpellWarningDialog() {
    const dialogRef = this.dialogService.open(CaseSpellingDialogComponent, {
      header: `Standardnamnet är på väg att skapas med versaler`,
      width: '',
      draggable: true,
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
    });

    if (!dialogRef) {
      return;
    }

    this.caseRef = dialogRef;

    return dialogRef.onClose.subscribe((res) => {
      if (res) {
        this.buildAndSaveNewStandardName();
      } else {
        this.loading = false;
      }
    });
  }

  protected canSaveNewStandardNamn() {
    if (this.form.value.selectedAtgard !== Atgard.NyttStandardnamn) {
      return false;
    }
    return !(
      this.form.value.selectedAtgard === Atgard.NyttStandardnamn &&
      !this.standardnamnInvalidLength() &&
      this.form.get('standardnamn')?.get('nyttnamn')?.value !== null &&
      this.form.get('standardnamn')?.get('nyttnamn')?.value !== ''
    );
  }

  async synonymisera(synonym: SynonymDTO) {
    this.saveSynonym(synonym);
  }

  saveSynonym(synonym: SynonymDTO) {
    this.form.valid && (this.loading = true);

    const successMessage = `Ny synonym för ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    } tillagd.`;
    const errorMessage = `Det gick inte att synonymisera ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    }.`;

    this.atgardsService
      .synonymisera(synonym, this.oversiktRow.type === NameType.Smittamne ? this.getNameType() : this.oversiktRow.type)
      .then(this.atgardaSuccessHandler(successMessage).bind(this))
      .catch(this.atgardaErrorHandler(errorMessage).bind(this))
      .finally(this.atgardaFinallyHandler.bind(this));
  }

  private getNameType() {
    return this.typeOfFynd;
  }

  ignorera(ignorering: IgnoreringDTO) {
    const successMessage = `Okänd ${this.oversiktRow.type?.toLocaleLowerCase()} ${this.oversiktRow.name} ignorerad.`;
    const errorMessage = `Det gick inte att ignorera ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    }.`;

    this.disableForm();

    this.atgardsService
      .ignorera(ignorering, this.oversiktRow.type as NameType)
      .then(this.atgardaSuccessHandler(successMessage).bind(this))
      .catch(this.atgardaErrorHandler(errorMessage).bind(this))
      .finally(this.atgardaFinallyHandler.bind(this));
  }

  parkera(parkering: ParkeringDTO) {
    const successMessage = `Okänd ${this.oversiktRow.type?.toLocaleLowerCase()} ${this.oversiktRow.name} parkerades.`;
    const errorMessage = `Det gick inte att parkera ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    }.`;

    this.disableForm();

    this.atgardsService
      .parkera(parkering, this.oversiktRow.type as NameType)
      .then(this.atgardaSuccessHandler(successMessage).bind(this))
      .catch(this.atgardaErrorHandler(errorMessage).bind(this))
      .finally(this.atgardaFinallyHandler.bind(this));
  }

  nyttStandardNamn(standardnamn: NyttStandardNamnDTO) {
    const successMessage = `Standardnamn för nya ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    } tillagd.`;
    const errorMessage = `Det gick inte att skapa nytt standardnamn för ${this.oversiktRow.type?.toLocaleLowerCase()} ${
      this.oversiktRow.name
    }.`;

    this.disableForm();

    this.atgardsService
      .nyttStandardNamn(standardnamn, this.oversiktRow.type as NameType)
      .then(this.atgardaSuccessHandler(successMessage).bind(this))
      .catch(this.atgardaErrorHandler(errorMessage).bind(this))
  }

  atgardaSuccessHandler(successMessage: string) {
    return (data: any) => {
      this.atgardResult = { result: 'success', message: successMessage };
      this.atgardaFinallyHandler();
    };
  }

  atgardaErrorHandler(errorMessage: string) {
    return (error: any) => {
      let message = '';
      if (error.status && error.status === 409) {
        message = 'Standardnamnent finns redan'
      } else {
        message = 'Ett okänt fel har inträffat'
      }
      this.loading = false;
      this.enableForm();
      this.atgardResult = { result: 'error', message };
    };
  }

  atgardaFinallyHandler() {
    this.loading = false;
    this.ref.close(this.atgardResult);
  }

  close() {
    this.ref.close();
  }

  get loadingText() {
    return `Åtgärdar ${this.loadingTextFromNameType(this.oversiktRow.type as NameType)}`;
  }

  private loadingTextFromNameType(nameType: NameType) {
    switch (nameType) {
      case NameType.Analys:
        return 'okänd analys';
      case NameType.Antibiotika:
        return 'okänd antibiotika';
      case NameType.Smittamne:
        return 'okänt smittämne';
      case NameType.Provmaterial:
        return 'okänt provmaterial';
      case NameType.NegativtFynd:
        return 'negativt fynd';
      default:
        return undefined;
    }
  }

  private disableForm() {
    this.form.get('selectedAtgard')?.disable();
    this.form.get('synonymiseraTill')?.disable();
    this.form.get('comment')?.disable();
    this.form.get('standardnamn')?.disable();
  }

  private enableForm() {
    this.form.get('selectedAtgard')?.enable();
    this.form.get('synonymiseraTill')?.enable();
    this.form.get('comment')?.enable();
    this.form.get('standardnamn')?.enable();
  }

  private updateStandardnamnList(nameType: NameType) {
    this.standardNamnService
      .getStandardNamnByType(nameType)
      ?.pipe(
        map((namn) => namn.filter((name) => name.status === 'AKTIV')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((list) => {
        this.standardNamnLista = list.sort((a, b) => (a.standardNamn > b.standardNamn ? 1 : -1));
      });
  }

  protected setTypeOfFynd(nameType: NameType) {
    this.typeOfFynd = nameType;
    this.updateStandardnamnList(nameType);
  }
}
